import { ComponentRegistry } from "./ComponentRegistry.js";

const commonWebComponentInitiatializer = async (configuration) => {
  const registry = new ComponentRegistry();

  // Note that this is an async process since we need to grab the manifest
  await registry.loadConfiguration({
    ...configuration,
    apps: decorateManifestEntries({
      ...(configuration.apps || {})
    }),
    bundles: decorateManifestEntries(configuration.bundles || {})
  });

  return registry;
};

/**
 * Registers the listener for new FNC components being added to the DOM,
 * pre-loads any components or bundles configured to be loaded automatically,
 * and registers watchers to see when any "lazy loaded" elements need their
 * assets imported.
 * 
 * @param {import("./types.d.ts").FncConfig} configuration 
 * @returns 
 */
export const initWebComponents = async (configuration) => {
  const registry = await commonWebComponentInitiatializer(configuration);

  return registry;
};

/**
 * Performs the same functions as initWebComponents(), but intended for development environments
 * (e.g., Storybook or Vite dev servers) where FNC_* environment variables could potentially be 
 * available. Calls ready() immediately unless `configuration.skipReady` is truthy
 * 
 * @param {import("./types.d.ts").FncConfig} configuration 
 * @returns 
 */
export const initWebComponentsForDev = async (configuration) => {
  const { FNC_API_URL, FNC_DEFAULT_LOCALE } = import.meta.env || {};

  const registry = await commonWebComponentInitiatializer({
    ...configuration,
    debug: true,

    // Fill in missing values with environment variables
    apiUrl: configuration.apiUrl || FNC_API_URL,
    defaultLocale: configuration.defaultLocale || FNC_DEFAULT_LOCALE || "en-us"
  });

  if (!configuration.skipReady) {
    await registry.ready();
  }
  return registry;
};

/**
 * The `manifest.json` is going to come with links relative to the location of the bundle that
 * this logic lives in. This function "decorates" the manifest with the complete path.
 * 
 * @param {*} manifestEntry 
 * @param {*} rel The `import.meta.url` of the file to which everything is relative
 * @returns 
 */
export const decorateManifestEntries = (manifestEntry, rel = import.meta.url) => {
  Object.keys(manifestEntry).forEach(key => {
    manifestEntry[key].entrypoint = new URL(manifestEntry[key].entrypoint, rel).toString();
    if (manifestEntry[key].css) {
      manifestEntry[key].css = manifestEntry[key].css.map(css => new URL(css, rel).toString())
    }
    if (manifestEntry[key].translations) {
      manifestEntry[key].translations = new URL(manifestEntry[key].translations, rel).toString();
    }
  });
  return manifestEntry;
};
